import { useState, useCallback, useRef } from 'react';
import { nanoid } from 'nanoid';
import { Message } from '../types/chat';
import { useHealthMetrics } from './useHealthMetrics';
import { generateResponse } from '../utils/aiCoach/responseGenerator';
import { triggerHapticFeedback } from '../utils/haptics';
import { getRandomWelcomeMessage } from '../utils/welcomeMessages';

interface UseAICoachOptions {
  onError?: (error: Error) => void;
  onTypingComplete?: () => void;
}

export function useAICoach(options?: UseAICoachOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const processingRef = useRef(false);
  const metrics = useHealthMetrics();

  const addMessage = useCallback((message: Omit<Message, 'id' | 'timestamp'>) => {
    const newMessage: Message = {
      ...message,
      id: nanoid(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  const sendMessage = useCallback(async (content: string, isWelcome: boolean = false) => {
    if (processingRef.current && !isWelcome) return;
    if (!isWelcome && !content.trim()) return;
    
    try {
      processingRef.current = true;
      setIsTyping(true);
      
      if (!isWelcome) {
        // Add user message
        addMessage({
          type: 'user',
          content: content.trim()
        });
      }

      // Add AI message with typing indicator
      const aiMessageId = nanoid();
      const messageContent = isWelcome 
        ? getRandomWelcomeMessage()
        : generateResponse({
            metrics,
            messageHistory: messages.map(m => m.content),
            currentQuery: content.trim()
          });

      addMessage({
        id: aiMessageId,
        type: 'ai',
        content: messageContent,
        isTyping: true
      });

      // Simulate typing delay
      await new Promise(resolve => setTimeout(resolve, isWelcome ? 1500 : 2000));

      // Update message to remove typing indicator
      setMessages(prev => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? { ...msg, isTyping: false }
            : msg
        )
      );

      if (!isWelcome) {
        triggerHapticFeedback();
      }

    } catch (error) {
      console.error('Error in AI coach:', error);
      options?.onError?.(error as Error);
      
      addMessage({
        type: 'ai',
        content: 'I apologize, but I encountered an error. Please try again.',
        isTyping: false
      });
    } finally {
      setIsTyping(false);
      processingRef.current = false;
      options?.onTypingComplete?.();
    }
  }, [messages, metrics, addMessage, options]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isTyping,
    sendMessage,
    clearMessages
  };
}